import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { create } from 'zustand';
import { REVIEW_API, referenceOf, type Review, type ReviewState } from '@chess/api-review';
import type { ShortlistReading } from '@chess/api-review/board';
import type { ConstraintScope } from '@chess/api-constraints';
import { useConstraint, useConstraintRegistry } from '@chess/api-constraints/react';
import { readingFor, type Reading } from './reading.ts';
import './score-bar.css';

// The evaluation bar beside the board, as v1 draws it: the board's height,
// filling from the bottom with the bottom player's advantage, a line across
// the middle to measure against, and the score as a small label. It reads
// the review's stored scan for the position it is on, takes the shortlist's
// deeper live reading when that is about the same position, and while the
// reader is exploring a line of their own it has only the live reading.
// A finished game's last position is read off the result, not the engine.
// The board's Score bar visibility constraint (a puzzle) blanks it: the bar
// keeps its place beside the board and says nothing.

interface BarState {
  reading?: Reading;
  /** Whether the reader's end of the bar is its bottom, as the board is oriented. */
  readerAtBottom: boolean;
  /** The constraint scope of the board the bar stands beside. */
  scope?: ConstraintScope;
  set(reading: Reading | undefined, readerAtBottom: boolean): void;
}
const useBar = create<BarState>((set) => ({
  reading: undefined,
  readerAtBottom: true,
  set: (reading, readerAtBottom) => set({ reading, readerAtBottom }),
}));

export function setup(app: PiletApi) {
  let state: ReviewState = { status: 'idle', ply: 0 };
  let shortlist: ShortlistReading = {};

  const refresh = () => {
    const sides = state.game?.sides;
    useBar.getState().set(readingFor(state, shortlist), referenceOf(sides) === (sides?.orientation ?? 'white'));
  };

  void app.getApi<Review>(REVIEW_API).then((review) => {
    const onReview = (next: ReviewState) => {
      state = next;
      if (useBar.getState().scope !== next.scope) useBar.setState({ scope: next.scope });
      refresh();
    };
    onReview(review.state());
    review.subscribe(onReview);
  });
  app.on('review.shortlist', (event: ShortlistReading) => {
    shortlist = event;
    refresh();
  });

  const ScoreBar: React.FC = () => {
    const registry = useConstraintRegistry(app);
    const scope = useBar((s) => s.scope);
    const visible = useConstraint(registry, scope, 'scoreBarVisibility').visible;
    const read = useBar((s) => s.reading);
    const reading = visible ? read : undefined;
    const readerAtBottom = useBar((s) => s.readerAtBottom);
    // The reading's share is the reader's; the fill rises from the bottom, so a
    // board with the reader on top fills with the opponent's share instead.
    const readerShare = Math.min(Math.max(reading?.share ?? 0.5, 0), 1);
    const share = readerAtBottom ? readerShare : 1 - readerShare;
    const label = !visible ? 'Hidden' : (reading?.text ?? 'Not known yet');
    return (
      <div
        className={`score-bar${reading ? '' : ' score-bar-unknown'}`}
        role="img"
        aria-label={`Advantage ${label}`}
        title={label}>
        <div className="score-bar-top" />
        <div className="score-bar-bottom" style={{ height: `${share * 100}%` }} />
        <div className="score-bar-mid" />
        <span className={`score-bar-label${share >= 0.5 ? ' score-bar-label-bottom' : ' score-bar-label-top'}`}>
          {reading?.text ?? ''}
        </span>
      </div>
    );
  };

  app.registerExtension('review.boardSide', ScoreBar, { order: 10 });
}
